import { CZECH_MONTHS } from '../data/czechCalendar';
import type { CalendarPage, CalendarPhoto, CalendarProject } from '../types/calendarProject';

const coverTitles = {
  basic_year_story: 'Tvůj rok na zdi',
  family_planner_cz: 'Rodinný rok',
  fineart_gallery: 'Galerie roku',
};

const themeLayouts = {
  clean_gallery: 'large-photo-clean',
  warm_family: 'family-planner',
  premium_blue: 'premium-blue',
};

const defaultCrop = { x: 0.5, y: 0.5, zoom: 1, rotation: 0 };

function getPageStatus(photo: CalendarPhoto | undefined, isFallback: boolean): CalendarPage['preflightStatus'] {
  if (!photo) return 'error';
  if (isFallback || photo.score < 75) return 'warning';
  return 'ok';
}

function getMonthYear(project: CalendarProject, monthOffset: number) {
  const zeroBasedStartMonth = project.period.startMonth - 1;
  const absoluteMonth = zeroBasedStartMonth + monthOffset;
  return {
    monthIndex: (absoluteMonth % 12) + 1,
    year: project.period.year + Math.floor(absoluteMonth / 12),
  };
}

export function createMagicDraft(project: CalendarProject): CalendarProject {
  const selectedPhotos = project.photos
    .filter((photo) => photo.selectedForDraft)
    .sort((a, b) => b.score - a.score || b.sizeBytes - a.sizeBytes);
  const pickedPhotos = selectedPhotos.slice(0, 13);
  const layoutId = themeLayouts[project.themeId];
  const pages: CalendarPage[] = [];
  const assignedPhotoIds = new Set<string>();

  const pickPhoto = (index: number) => {
    if (!pickedPhotos.length) return { photo: undefined, isFallback: false };
    const isFallback = index >= pickedPhotos.length;
    const photo = pickedPhotos[index % pickedPhotos.length];
    if (!isFallback) assignedPhotoIds.add(photo.id);
    return { photo, isFallback };
  };

  const coverPick = pickPhoto(0);
  pages.push({
    id: 'cover',
    type: 'cover',
    title: coverTitles[project.productId],
    subtitle: 'Titulka',
    photoId: coverPick.photo?.id,
    layoutId,
    crop: defaultCrop,
    preflightStatus: getPageStatus(coverPick.photo, coverPick.isFallback),
  });

  for (let monthOffset = 0; monthOffset < 12; monthOffset += 1) {
    const { monthIndex, year } = getMonthYear(project, monthOffset);
    const monthPick = pickPhoto(monthOffset + 1);
    pages.push({
      id: `month_${String(monthOffset + 1).padStart(2, '0')}`,
      type: 'month',
      monthIndex,
      year,
      title: CZECH_MONTHS[monthIndex - 1],
      subtitle: String(year),
      photoId: monthPick.photo?.id,
      layoutId,
      crop: defaultCrop,
      preflightStatus: getPageStatus(monthPick.photo, monthPick.isFallback),
    });
  }

  const photos = project.photos.map((photo) => {
    const assignedPage = pages.find((page) => page.photoId === photo.id && assignedPhotoIds.has(photo.id));
    return {
      ...photo,
      assignedPageId: assignedPage?.id,
    };
  });

  return {
    ...project,
    pages,
    photos,
    updatedAt: new Date().toISOString(),
  };
}
